import { expValEquals } from '@confluence/feature-experiments';
import { getLogger } from '@confluence/logger';
import { preloadEditorPageCoverPicture } from '@confluence/page-cover-picture/entry-points/preloadEditorPageCoverPicture.ts';
import { preloadEmojiTitle } from '@confluence/emoji-title/entry-points/preloadEmojiTitle.ts';
import { preloadEditorContentState } from '@confluence/content-state/entry-points/preloadEditorContentState.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { preloadDraftContentAppearance } from '@confluence/content-appearance/entry-points/preloadDraftContentAppearance.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { preloadContentMode } from '@confluence/editor-content-mode-button/entry-points/preloadContentMode.ts';
/* eslint-disable-next-line import/no-extraneous-dependencies */
import { preloadLastUpdated } from '@confluence/content-header/entry-points/preloadLastUpdated.ts';
import { preloadEditorPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadEditorPageTitleContentProperties.ts';
import { preloadMentionsProvider } from '@confluence/fabric-media-support/entry-points/preloadMentions.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime.ts';
import { SYSTEM_CONTENT_BYLINE } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { fg } from '@confluence/feature-gating';
import { isCollabDraftCacheStale } from '@confluence/fabric-providers/entry-points/CollabDraftCacheManager.ts';

import { preloadCollabDraftContent, preloadNativeCollab } from './preloadNativeEditor.ts';
import { preloadEditorNodeDataProvidersData } from './preloadEditorNodeDataProvidersData.ts';
import type { IPreloadEditorData } from './types.ts';
import { preloadEditorSyncedBlocksData } from './preloadEditorSyncedBlocksData.ts';

const logger = getLogger('load-edit-page');

/**
 * For preloading the editor of a draft.
 * Live Pages will have its own editor preloading.
 */
export async function preloadEditorData({
	spaceKey,
	contentId,
	shouldPreloadMentionsProvider = false,
	useCache = false, // use cache for native editor data
	catchFn,
	isBlog,
	cloudId,
	isLivePage = false,
}: IPreloadEditorData): Promise<void> {
	// note: explicitly casting to Promise<any> to avoid typescript incorrectly inferring tuple types; although we don't use the return value of this function regardless
	logger.debug`Calling preloadEditorData ${performance.now() / 1000}`;
	const draftShareId = getDraftShareIdFromQueryParam();
	// preload queries shared by Synchrony and Native Collab editors
	const immediateResultsPromises: Array<Promise<any> | undefined> = [
		preloadDraftContentAppearance(contentId, useCache),
	];

	// specific native collab editor queries
	if (!!contentId) {
		immediateResultsPromises.push(
			preloadCollabDraftContent(
				contentId,
				draftShareId,
				expValEquals('platform_editor_collabdraft_query_improvements', 'isEnabled', true)
					? useCache || !isCollabDraftCacheStale(contentId, draftShareId)
					: useCache,
			),
		);
		// synced blocks data preloading - feature gated inside the function
		if (!fg('platform_synced_block_ssr_v2')) {
			immediateResultsPromises.push(preloadEditorSyncedBlocksData(contentId, cloudId, isBlog));
		}

		immediateResultsPromises.push(preloadEditorNodeDataProvidersData(contentId, cloudId, isBlog));

		if (
			expValEquals('confluence_compact_text_format', 'isEnabled', true) ||
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			(expValEquals('cc_editor_ai_content_mode', 'variant', 'test') &&
				fg('platform_editor_content_mode_button_mvp'))
		) {
			const { isArchived } = getCachedPageData(contentId ?? undefined);
			immediateResultsPromises.push(
				preloadContentMode({ contentId, isDraft: !isLivePage, useCache, isArchived }),
			);
		}

		immediateResultsPromises.push(
			preloadEmojiTitle(contentId, draftShareId, useCache),
			preloadNativeCollab(contentId, draftShareId, useCache),
		);
	}

	immediateResultsPromises.push(
		preloadEditorPageCoverPicture(contentId, draftShareId, useCache),
		preloadEditorContentState({
			contentId,
			useCache,
			shouldGetDraftVersion: !isLivePage,
		}),
	);

	if (isLivePage) {
		// Pre-loading apps in the byline area for live pages. Currently only live pages have extension point apps and not classic editor.
		immediateResultsPromises.push(
			preloadWebItemLocation({ contentId, location: SYSTEM_CONTENT_BYLINE }),
			// For some reason the addition of the filter (uses new Forge query) is causing SSR to timeout - workaround by skipping the filter
			preloadUseExtensionList({
				spaceKey: spaceKey || '',
				moduleType: FORGE_MODULE_BYLINE,
			}),
		);

		immediateResultsPromises.push(preloadLastUpdated(contentId, useCache));

		immediateResultsPromises.push(preloadReadTime({ contentId, useCache }));
	}

	if (shouldPreloadMentionsProvider) {
		immediateResultsPromises.push(preloadMentionsProvider());
	}

	immediateResultsPromises.push(
		preloadEditorPageTitleContentProperties({
			contentId,
			draftShareId,
			useCache,
		}),
	);

	await waitForAllReturnWhenError(
		immediateResultsPromises.map((p) => (!!catchFn ? p?.catch(catchFn) : p)),
	);

	logger.debug`preloadEditorData complete ${performance.now() / 1000}`;
}
